import { DateTime } from "luxon";

import type { IIndivid } from "@/composables/KundserviceAppApiClient";

export const formatAsYesOrNo = (value?: boolean | string | null) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return value ? "Ja" : "Nej";
};

type Currency = "SEK";
type Language = "sv-SE";

export const formattedAmountInCurrency = (
  amount?: number | null,
  currency: Currency = "SEK",
  language: Language = "sv-SE",
) => {
  if (amount === undefined) return undefined;
  if (amount !== 0 && (!amount || Number.isNaN(amount))) {
    return "-";
  }

  const maximumFractionDigits = 0;

  const formatter = new Intl.NumberFormat(language, {
    style: "currency",
    currency: currency,
    maximumFractionDigits,
  });
  return formatter.format(amount);
};

export const formatRelativeDate = (date?: Date | null) => {
  if (!date) return "-";
  const dt = DateTime.fromJSDate(date).setLocale("sv");
  return `${dt.toFormat(standardDateFormat)} (${dt.toRelativeCalendar({
    unit: "days",
  })})`;
};

export const formatMonthsToString = (months?: number | null) => {
  if (months === undefined) return undefined;
  return typeof months !== "number" ? "" : months + " mån";
};

export const formatMonthsToYearPlusMonthsString = (months?: number | null) => {
  if (months === undefined) return undefined;
  if (months == null || Number.isNaN(months)) {
    return "";
  }
  if (months === 0) return "";

  const years = Math.floor(months / 12);
  const restMonths = months % 12;

  let formattedString = years ? `${years} år` : "";
  if (years && restMonths) formattedString += ", ";
  if (restMonths) formattedString += restMonths + " mån";
  return `(${formattedString})`;
};

export const formatIndividFullName = (individ: IIndivid | null) => {
  const nameParts = [
    individ?.tilltalsnamn ?? individ?.fornamn,
    individ?.mellannamn,
    individ?.efternamn,
  ].filter((x): x is string => !!x);

  return nameParts.join(" ");
};

export const formatStandardDate = (date: Date | undefined | null) => {
  if (!date) return "";
  return `${DateTime.fromJSDate(date).toFormat(standardDateFormat)}`;
};

export const formatYear = (date: Date | undefined | null) => {
  if (!date) return "";
  return `${DateTime.fromJSDate(date).toFormat("y")}`;
};

export const standardDateFormat = "d LLL y";

export const formatDateToYYYYMMDD = (date: Date | undefined | null) => {
  return !date
    ? ""
    : DateTime.fromJSDate(date).setLocale("sv").toFormat("yyyy-MM-dd");
};

export const formatPostnummer = (postnummer: string) => {
  return postnummer.length === 5
    ? [postnummer.slice(0, -2), " ", postnummer.slice(-2)].join("")
    : postnummer;
};

export const personnummerWithDash12Digits = (
  personNumber: string | null | undefined,
) => {
  if (!personNumber) {
    return undefined;
  }

  if (personNumber.length === 13) return personNumber;

  return [personNumber.slice(0, -4), "-", personNumber.slice(-4)].join("");
};

export const orgNumberWithDash = (orgNumber?: string) => {
  return orgNumber && orgNumber.length >= 10 && orgNumber.indexOf("-") === -1
    ? [orgNumber.slice(0, -4), "-", orgNumber.slice(-4)].join("")
    : orgNumber;
};
